
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type {
  TrendingData,
  TrendingDataPoint,
} from '@empathybroker/tsclient-stats/dist/models/trendingreport/trendingdata'
import type { Bubble, BubblesClusterConfig, Params } from '@empathyco/insights'
import type { PropType } from 'vue'
import type { ExplainMessages } from '../../../models'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { BubblesClusterComponent, BubblesClusterOrientationEnum } from '@empathyco/insights'

import { defineComponent } from 'vue'
import { mapActions } from 'vuex'

import { KpiEnum } from '../../../enums'

import { capitalizeSentence } from '../../../filters'

import { InsightsMixin } from '../../../mixins'
import { CONTEXT_CONSTS } from '../../../store/context/context.consts'

export interface Data {
  trending: TrendingData
}

export default defineComponent({
  name: 'ExplainRealtimeQueries',
  components: {},
  mixins: [InsightsMixin],
  props: {
    messages: {
      type: Object as PropType<ExplainMessages>,
      required: true,
    },
    queryCount: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      baseClass: 'epb-explain-realtime-queries',
      fetchDataInterval: undefined,
      component: undefined,
      isComponentVisible: false,
    }
  },
  computed: {
    componentParams(): Params<BubblesClusterConfig, Bubble[]> {
      const length = this.componentData.length || 1
      return {
        config: {
          valueLabel: capitalizeSentence(
            this.$epb.messages.insights.analytics.kpis[KpiEnum.QUERIES].valueLabel,
          ),
          orientation: BubblesClusterOrientationEnum.FULL,
          transitionsDurationInMillis: 1000,
          delayTransitionInMillis: 1200 / length,
          animated: true,
        },
        data: this.componentData,
      }
    },
    componentData(): Bubble[] {
      const queries = [...(this.data?.trending?.queries ?? [])]
        .sort(
          (trendingDataPoint1: TrendingDataPoint, trendingDataPoint2: TrendingDataPoint) =>
            trendingDataPoint2.count - trendingDataPoint1.count,
        )
        .slice(0, this.queryCount)
      return queries.map((trendingDataPoint: TrendingDataPoint) => ({
        name: trendingDataPoint.query,
        value: trendingDataPoint.count,
      }))
    },
  },
  watch: {
    componentParams: {
      handler(params: Params<BubblesClusterConfig, Bubble[]>): void {
        if (this.isComponentVisible) {
          this.component.update(params)
        }
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new BubblesClusterComponent()
    this.component.onBubbleClick = (bubble: Bubble) => this.setQuery(bubble.name)
  },
  mounted(): void {
    this.fetchDataInterval = setInterval(async () => {
      if (this.isComponentVisible) {
        this.data = await this.fetchData()
      }
    }, 30000)
  },
  beforeDestroy(): void {
    if (this.fetchDataInterval) {
      clearInterval(this.fetchDataInterval)
    }

    if (this.isComponentVisible) {
      this.component.destroy()
    }
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
    }),
    componentAfterEnter(): void {
      this.component.init(this.$refs.component, this.componentParams)
      this.isComponentVisible = true
    },
    componentLeave(): void {
      if (this.isComponentVisible) {
        this.component.destroy()
        this.isComponentVisible = false
      }
    },
    async fetchData(): Promise<Data> {
      const trendingResponse: StatsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.TRENDING,
        params: {},
      })
      return {
        trending: trendingResponse.data,
      }
    },
  },
})
